import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import Mixpanel from 'helpers/Mixpanel.js';
import { Transition } from 'react-spring/renderprops.cjs';
import { REMOVE_RICH_BRAND_DATA, REMOVE_RICH_PUBLISHER_DATA } from 'components/Global/Filters/actions.js';
import { articleLengthToWordCount } from 'helpers/Utils.js';
import Tag from 'components/Global/Tag/Tag.jsx';
import GrowContainer from 'components/Global/GrowContainer/GrowContainer.jsx';
import { SelectedFilterContainer, TagContainer, ClearFilterButton, AnimationContainer } from './styled';

const constructTransitionContainer = (filterItems) => (
  <Transition
    items={filterItems}
    keys={item => item.props.data}
    from={{ overflow: 'hidden', width: 0, opacity: 0 }}
    enter={{ width: 'auto', opacity: 1 }}
    leave={{ width: 0, opacity: 0 }}
    trail={200}
  >
    {item => styles => <AnimationContainer {...styles}>{item}</AnimationContainer>}
  </Transition>);

export class SelectedFiltersBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      initialStartDate: moment().subtract(1, 'year').format('YYYY-MM-DD'),
      initialEndDate: moment().format('YYYY-MM-DD'),
    };
  }

  filtersSelected() {
    const { filters, hiddenFilters = [] } = this.props;
    let selected = false;

    if (!filters) {
      return selected;
    }

    const isHiddenFilters = filter => [
      ...hiddenFilters,
      'timeInterval',
      'pageNum',
      'startDate',
      'endDate',
      'pageSize',
      'searchBy',
      'sort_order',
      'sort_by'].indexOf(filter) !== -1;

    const hasDateFilters =
      (filters.startDate && filters.endDate) &&
      ((filters.startDate !== this.state.initialStartDate) || (filters.endDate !== this.state.initialEndDate));

    for (const filter in filters) {
      const value = filters[filter];
      if (value && !isHiddenFilters(filter)) {
        selected = selected || value.length > 0;
      }
    }

    return selected || hasDateFilters;
  }

  clearAllFilters() {
    const { filters, disabledDelete = [], hiddenFilters = [], setFilters, updateRichData } = this.props;
    const { initialStartDate, initialEndDate } = this.state;

    const {
      searchBy,
      pageSize,
    } = filters;

    let disableClear = {};

    for (const filter in filters) {
      if (disabledDelete.indexOf(filter) !== -1 || hiddenFilters.indexOf(filter) !== -1) {
        disableClear = { [filter]: filters[filter], ...disableClear };
      }
    }

    Mixpanel.track('clicked Clear All on Selected Filters Bar');
    setFilters({ ...disableClear, startDate: initialStartDate, endDate: initialEndDate, pageNum: 1, pageSize, searchBy });
    updateRichData(REMOVE_RICH_BRAND_DATA);
    updateRichData(REMOVE_RICH_PUBLISHER_DATA);
  }

  removeEntity(id, type) {
    const {
      updateRichData,
      updateListFilter,
      filters: {
        publisher_ids: publishers = [],
        brand_ids: brands = [],
      },
    } = this.props;

    let actionType;
    let entityList;
    let key;

    if (type === 'brand') {
      actionType = REMOVE_RICH_BRAND_DATA;
      key = 'brand_ids';
      entityList = brands;
    } else {
      actionType = REMOVE_RICH_PUBLISHER_DATA;
      key = 'publisher_ids';
      entityList = publishers;
    }

    Mixpanel.track(`removed ${type} from Selected Filter Bar`);
    updateRichData(actionType, id);
    updateListFilter(key, entityList, selected => selected !== id);
  }

  render() {
    const {
      setFilters,
      updateListFilter,
      filters,
      hubsList,
      disabledDelete = [],
      hiddenFilters = [],
      richBrandData = [],
      richPublisherData = [],
    } = this.props;
    const { initialStartDate, initialEndDate } = this.state;

    const hasSelectedFilters = this.filtersSelected();

    const {
    keyword_tags = [],
    startDate = '',
    endDate = '',
    categories = [],
    publisher_ids: publishers = [],
    brand_ids: brands = [],
    hub_ids: hubs = [],
    titles = [],
    phrases = [],
    content_type: contentType = '',
    searchQuery,
    pageSize,
    article_length,
    content_format,
  } = filters;

    const richDataHubs = hubsList.data.filter(hub => hubs.includes(hub.public_id));
    const richBrandSelectedList = richBrandData.filter((entity) => brands.includes(entity.public_id));
    const richPublisherSelectedList = richPublisherData.filter((entity) => publishers.includes(entity.public_id));

    const searchFilter = searchQuery && !hiddenFilters.includes('searchQuery') ?
    [(<Tag
      selected
      type="search"
      deleteEnabled={!disabledDelete.includes('search')}
      data={searchQuery}
      onClick={() => {
        Mixpanel.track('removed Search Query from Selected Filters Bar', { query: searchQuery });
        return setFilters({ ...filters, searchQuery: '', searchBy: '', pageNum: 1, pageSize });
      }}
    />)] : [];

    const dateFilter = ((startDate && endDate) && (startDate !== initialStartDate || endDate !== initialEndDate)) ?
    [(<Tag
      selected
      type="date"
      deleteEnabled={!disabledDelete.includes('date')}
      data={`${startDate} → ${endDate}`}
      onClick={() => {
        Mixpanel.track('removed Date filter from Selected Filters Bar');
        return setFilters({ ...filters, startDate: initialStartDate, endDate: initialEndDate, pageNum: 1, pageSize });
      }}
    />)] : [];

    const categoryFilters = categories && !hiddenFilters.includes('categories') ? categories.map(category =>
      <Tag
        selected
        type="category"
        deleteEnabled={!disabledDelete.includes('category')}
        data={category}
        onClick={() => {
          Mixpanel.track('removed Category from Selected Filters Bar');
          return updateListFilter('categories', categories, selectedCategory => selectedCategory !== category);
        }}
      />) : [];

    const keywordFilters = keyword_tags && !hiddenFilters.includes('keyword_tags') ? keyword_tags.map(keyword =>
      <Tag
        selected
        type="keyword"
        deleteEnabled={!disabledDelete.includes('keyword_tags')}
        data={keyword}
        onClick={() => {
          Mixpanel.track('removed Keyword from Selected Filters Bar');
          return updateListFilter('keyword_tags', keyword_tags, selectedKeyword => selectedKeyword !== keyword);
        }}
      />) : [];

    const publisherFilters = publishers && !hiddenFilters.includes('publishers') ? richPublisherSelectedList.map(publisher =>
    (<Tag
      selected
      type="competitor"
      deleteEnabled={!disabledDelete.includes('competitor')}
      data={publisher.name}
      icon={publisher.logo_url}
      onClick={() => this.removeEntity(publisher.public_id, 'publisher')}
    />)) : [];

    const brandFilters = brands && !hiddenFilters.includes('brands') ? richBrandSelectedList.map(brand =>
      <Tag
        selected
        type="competitor"
        deleteEnabled={!disabledDelete.includes('competitor')}
        data={brand.name}
        icon={brand.logo_url}
        onClick={() => this.removeEntity(brand.public_id, 'brand')}
      />) : [];

    const hubsFilters = hubs && !hiddenFilters.includes('hubs') ? richDataHubs.map(hub =>
      <Tag
        selected
        type="competitor"
        deleteEnabled={!disabledDelete.includes('competitor')}
        data={hub.name}
        icon={hub.logo_url}
        onClick={() => {
          Mixpanel.track('removed Hub from Selected Filters Bar');
          return updateListFilter('hub_ids', hubs, selectedHub => selectedHub !== hub.public_id);
        }}
      />) : [];

    const titleFilters = titles && !hiddenFilters.includes('search') ? titles.map(title =>
    (<Tag
      selected
      type="search"
      deleteEnabled={!disabledDelete.includes('search')}
      data={title}
      onClick={() => {
        Mixpanel.track('removed title from Selected Filters Bar');
        return updateListFilter('titles', titles, selectedTitle => selectedTitle !== title);
      }}
    />)) : [];

    const phraseFilters = phrases && !hiddenFilters.includes('search') ? phrases.map(phrase =>
    (<Tag
      selected
      type="search"
      deleteEnabled={!disabledDelete.includes('search')}
      data={phrase}
      onClick={() => {
        Mixpanel.track('removed search phrase from Selected Filters Bar');
        return updateListFilter('phrases', phrases, selectedPhrase => selectedPhrase !== phrase);
      }}
    />)) : [];

    const contentFilter = contentType && !hiddenFilters.includes('content_type') ?
    [(<Tag
      selected
      type="content"
      deleteEnabled={!disabledDelete.includes('content_type')}
      data={contentType}
      onClick={() => {
        Mixpanel.track('removed content type from Selected Filters Bar');
        return setFilters({ ...filters, content_type: '' });
      }}
    />)] : [];

    const articleLengthFilter = article_length && !hiddenFilters.includes('article_length') ?
    [(<Tag
      selected
      type="content"
      deleteEnabled={!disabledDelete.includes('article_length')}
      data={articleLengthToWordCount[article_length]}
      onClick={() => {
        Mixpanel.track('removed article length from Selected Filters Bar');
        return setFilters({ ...filters, article_length: '' });
      }}
    />)] : [];

    const articleFormatFilter = content_format && !hiddenFilters.includes('content_format') ?
    [(<Tag
      selected
      type="content"
      deleteEnabled={!disabledDelete.includes('content_format')}
      data={content_format}
      onClick={() => {
        Mixpanel.track('removed content format from Selected Filters Bar');
        setFilters({ ...filters, content_format: '' });
      }}
    />)] : [];

    return (
      <GrowContainer type="height" from={0} to={'auto'} toggle={hasSelectedFilters} style={{ display: 'block', width: '100%' }}>
        <SelectedFilterContainer>
          <TagContainer>
            {constructTransitionContainer(searchFilter)}
            {constructTransitionContainer(dateFilter)}
            {constructTransitionContainer(categoryFilters)}
            {constructTransitionContainer(keywordFilters)}
            {constructTransitionContainer(publisherFilters)}
            {constructTransitionContainer(brandFilters)}
            {constructTransitionContainer(hubsFilters)}
            {constructTransitionContainer(titleFilters)}
            {constructTransitionContainer(phraseFilters)}
            {constructTransitionContainer(contentFilter)}
            {constructTransitionContainer(articleLengthFilter)}
            {constructTransitionContainer(articleFormatFilter)}
          </TagContainer>
          <ClearFilterButton onClick={() => this.clearAllFilters()}>Clear All</ClearFilterButton>
        </SelectedFilterContainer>
      </GrowContainer>);
  }
}

const mapDispatchToProps = (dispatch, { filterAction, filters }) => ({
  setFilters: (newFilters) => {
    dispatch({ type: filterAction, filters: {} });
    dispatch({ type: filterAction, filters: newFilters });
  },
  updateListFilter: (key, selected, filterCondition) => dispatch({
    filters: {
      [key]: selected.filter(selectedItem => filterCondition(selectedItem)),
      pageNum: 1,
      pageSize: filters.pageSize,
    },
    type: filterAction,
  }),
  updateRichData: (type, id) => dispatch({ type, id }),
});

const mapStateToProps = ({
  PublishersList: { publishersList, richPublisherData },
   BrandsList: { brandsList, richBrandData },
  HubsList: { hubsList },
}) => ({ brandsList, publishersList, hubsList, richPublisherData, richBrandData });

SelectedFiltersBar.sampleProps = {
  filters: {
    keyword_tags: ['tag 1', 'tag 2'],
    searchString: 'search value',
    startDate: '01-01-2000',
    endDate: '01-01-2020',
    categories: ['cat 1', 'cat 2'],
    publishers: [
      { public_id: '1', name: 'publisher 1', logo_url: '/blueprint/images/mockBrandIcon.png' },
      { public_id: '2', name: 'publisher 2', logo_url: '/blueprint/images/mockBrandIcon.png' }],
    brands: [
      { public_id: '3', name: 'brand 1', logo_url: '/blueprint/images/mockBrandIcon.png' },
      { public_id: '4', name: 'brand 2', logo_url: '/blueprint/images/mockBrandIcon.png' }],
  },
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectedFiltersBar);
